import type {
  UIMessage as ChatMessage,
  StreamTextOnFinishCallback,
  ToolSet
} from "ai";
import { generateId } from "ai";
import { Agent, type AgentContext, type Connection, type WSMessage } from "./";
import type { IncomingMessage, OutgoingMessage } from "./ai-types";
import { MessageType } from "./ai-types";

const decoder = new TextDecoder();

/**
 * Extension of Agent with built-in chat capabilities
 * @template Env Environment type containing bindings
 */
export class AIChatAgent<Env = unknown, State = unknown> extends Agent<
  Env,
  State
> {
  /**
   * Map of message `id`s to `AbortController`s
   * useful to propagate request cancellation signals for any external calls made by the agent
   */
  private _chatMessageAbortControllers: Map<string, AbortController>;
  /** Array of chat messages for the current conversation */
  messages: ChatMessage[];
  constructor(ctx: AgentContext, env: Env) {
    super(ctx, env);
    this.sql`create table if not exists cf_ai_chat_agent_messages (
      id text primary key,
      message text not null,
      created_at datetime default current_timestamp
    )`;
    this.messages = (
      this.sql`select * from cf_ai_chat_agent_messages` || []
    ).map((row) => {
      return JSON.parse(row.message as string);
    });

    this._chatMessageAbortControllers = new Map();
  }

  private _broadcastChatMessage(message: OutgoingMessage, exclude?: string[]) {
    this.broadcast(JSON.stringify(message), exclude);
  }

  override async onMessage(connection: Connection, message: WSMessage) {
    if (typeof message === "string") {
      let data: IncomingMessage;
      try {
        data = JSON.parse(message) as IncomingMessage;
      } catch (_error) {
        // silently ignore invalid messages for now
        // TODO: log errors with log levels
        return;
      }
      if (
        data.type === "cf_agent_use_chat_request" &&
        data.init.method === "POST"
      ) {
        const {
          // method,
          // keepalive,
          // headers,
          body // we're reading this
          //
          // // these might not exist?
          // dispatcher,
          // duplex
        } = data.init;
        const parsedBody = JSON.parse(body as string);
        const incomingMessages = Array.isArray(parsedBody.messages)
          ? parsedBody.messages
          : Array.isArray(parsedBody.message)
            ? parsedBody.message
            : [parsedBody.message];
        const messages = [...this.messages, ...incomingMessages];
        this._broadcastChatMessage(
          {
            messages,
            type: MessageType.CF_AGENT_CHAT_MESSAGES
          },
          [connection.id]
        );

        await this.persistMessages(incomingMessages, [connection.id]);

        this.observability?.emit(
          {
            displayMessage: "Chat message request",
            id: data.id,
            payload: {},
            timestamp: Date.now(),
            type: "message:request"
          },
          this.ctx
        );

        const chatMessageId = data.id;
        const abortSignal = this._getAbortSignal(chatMessageId);

        return this._tryCatchChat(async () => {
          const uiMessageOnFinish = async (finalMessages: ChatMessage[]) => {
            const newMessages = this._messagesNotAlreadyInAgent(finalMessages);
            // Persist only the new messages, not the finalMessages
            await this.persistMessages(newMessages, [connection.id]);
            this._removeAbortController(chatMessageId);

            this.observability?.emit(
              {
                displayMessage: "Chat message response",
                id: data.id,
                payload: {},
                timestamp: Date.now(),
                type: "message:response"
              },
              this.ctx
            );
          };

          const onFinish: StreamTextOnFinishCallback<ToolSet> = async ({
            // biome-ignore lint/correctness/noUnusedFunctionParameters: callback may be used by implementing classes
            response
          }) => {
            // This is called when streamText completes
          };

          const response = await this.onChatMessage(
            onFinish,
            abortSignal ? { abortSignal } : undefined,
            uiMessageOnFinish
          );

          if (response) {
            await this._reply(data.id, response);
          } else {
            // Log a warning for observability
            console.warn(
              `[AIChatAgent] onChatMessage returned no response for chatMessageId: ${chatMessageId}`
            );
            // Send a fallback message to the client
            this._broadcastChatMessage(
              {
                body: "No response was generated by the agent.",
                done: true,
                id: data.id,
                type: MessageType.CF_AGENT_USE_CHAT_RESPONSE
              },
              [connection.id]
            );
          }
        });
      }
      if (data.type === "cf_agent_chat_clear") {
        this._destroyAbortControllers();
        this.sql`delete from cf_ai_chat_agent_messages`;
        this.messages = [];
        this._broadcastChatMessage(
          {
            type: MessageType.CF_AGENT_CHAT_CLEAR
          },
          [connection.id]
        );
      } else if (data.type === "cf_agent_chat_messages") {
        // replace the messages with the new ones
        this.sql`delete from cf_ai_chat_agent_messages`;
        this.messages = data.messages; // Set directly since we're replacing everything

        // Persist all messages to DB
        for (const message of data.messages) {
          this
            .sql`insert into cf_ai_chat_agent_messages (id, message) values (${
            message.id
          },${JSON.stringify(message)})`;
        }

        // Broadcast the new message set
        this._broadcastChatMessage(
          {
            messages: this.messages,
            type: MessageType.CF_AGENT_CHAT_MESSAGES
          },
          [connection.id]
        );
      } else if (data.type === "cf_agent_chat_request_cancel") {
        // propagate an abort signal for the associated request
        this._cancelChatRequest(data.id);
      }
    }
  }

  override async onRequest(request: Request): Promise<Response> {
    return this._tryCatchChat(() => {
      const url = new URL(request.url);
      if (url.pathname.endsWith("/get-messages")) {
        const messages = (
          this.sql`select * from cf_ai_chat_agent_messages` || []
        ).map((row) => {
          return JSON.parse(row.message as string);
        }) as ChatMessage[];
        return Response.json(messages);
      }
      return super.onRequest(request);
    });
  }

  private async _tryCatchChat<T>(fn: () => T | Promise<T>) {
    try {
      return await fn();
    } catch (e) {
      throw this.onError(e);
    }
  }

  /**
   * Handle incoming chat messages and generate a response
   * @param onFinish Callback to be called when the response is finished
   * @param options.signal A signal to pass to any child requests which can be used to cancel them
   * @param uiMessageOnFinish Callback to be called when UI message stream is finished
   * @returns Response to send to the client or undefined
   */
  async onChatMessage(
    // biome-ignore lint/correctness/noUnusedFunctionParameters: overridden later
    onFinish: StreamTextOnFinishCallback<ToolSet>,
    // biome-ignore lint/correctness/noUnusedFunctionParameters: overridden later
    options?: { abortSignal: AbortSignal | undefined },
    // biome-ignore lint/correctness/noUnusedFunctionParameters: overridden later
    uiMessageOnFinish?: (messages: ChatMessage[]) => Promise<void>
  ): Promise<Response | undefined> {
    throw new Error(
      "recieved a chat message, override onChatMessage and return a Response to send to the client"
    );
  }

  /**
   * Save messages on the server side and trigger AI response
   * @param messages Chat messages to save
   */
  async saveMessages(messages: ChatMessage[]) {
    await this.persistMessages(messages);
    const uiMessageOnFinish = async (finalMessages: ChatMessage[]) => {
      const newMessages = this._messagesNotAlreadyInAgent(finalMessages);
      // Only persist the new messages, not the finalMessages
      await this.persistMessages(newMessages, []);
    };
    const onFinish: StreamTextOnFinishCallback<ToolSet> = async ({
      // biome-ignore lint/correctness/noUnusedFunctionParameters: callback may be used by implementing classes
      response
    }) => {
      // This is called when streamText completes
    };
    const response = await this.onChatMessage(
      onFinish,
      undefined,
      uiMessageOnFinish
    );
    if (response) {
      // we're just going to drain the body
      // @ts-ignore TODO: fix this type error
      for await (const chunk of response.body!) {
        decoder.decode(chunk);
      }
      response.body?.cancel();
    }
  }

  async persistMessages(
    messages: ChatMessage[],
    excludeBroadcastIds: string[] = []
  ) {
    const processedMessages: ChatMessage[] = [];

    for (const message of messages) {
      let messageId = message.id;
      if (!messageId) {
        messageId = generateId();
        message.id = messageId;
      }
      if (messageId) {
        this.sql`
          INSERT INTO cf_ai_chat_agent_messages (id, message)
          VALUES (${messageId}, ${JSON.stringify(message)})
          ON CONFLICT(id) DO NOTHING
          `;

        processedMessages.push(message);
      }
    }

    // Update in-memory messages by merging new messages
    const newMessages = this._messagesNotAlreadyInAgent(processedMessages);
    this.messages = [...this.messages, ...newMessages];

    this._broadcastChatMessage(
      {
        messages: this.messages, // Broadcast all messages, not just the new ones
        type: MessageType.CF_AGENT_CHAT_MESSAGES
      },
      excludeBroadcastIds
    );
  }

  private _messagesNotAlreadyInAgent(messages: ChatMessage[]) {
    const existingIds = new Set(this.messages.map((message) => message.id));
    return messages.filter((message) => !existingIds.has(message.id));
  }

  private async _reply(id: string, response: Response) {
    // now take chunks out from dataStreamResponse and send them to the client
    return this._tryCatchChat(async () => {
      // @ts-expect-error TODO: fix this type error
      for await (const chunk of response.body!) {
        const body = decoder.decode(chunk);

        this._broadcastChatMessage({
          body,
          done: false,
          id,
          type: MessageType.CF_AGENT_USE_CHAT_RESPONSE
        });
      }

      this._broadcastChatMessage({
        body: "",
        done: true,
        id,
        type: MessageType.CF_AGENT_USE_CHAT_RESPONSE
      });
    });
  }

  /**
   * For the given message id, look up its associated AbortController
   * If the AbortController does not exist, create and store one in memory
   *
   * returns the AbortSignal associated with the AbortController
   */
  private _getAbortSignal(id: string): AbortSignal | undefined {
    // Defensive check, since we're coercing message types at the moment
    if (typeof id !== "string") {
      return undefined;
    }

    if (!this._chatMessageAbortControllers.has(id)) {
      this._chatMessageAbortControllers.set(id, new AbortController());
    }

    return this._chatMessageAbortControllers.get(id)?.signal;
  }

  /**
   * Remove an abort controller from the cache of pending message responses
   */
  private _removeAbortController(id: string) {
    this._chatMessageAbortControllers.delete(id);
  }

  /**
   * Propagate an abort signal for any requests associated with the given message id
   */
  private _cancelChatRequest(id: string) {
    if (this._chatMessageAbortControllers.has(id)) {
      const abortController = this._chatMessageAbortControllers.get(id);
      abortController?.abort();
    }
  }

  /**
   * Abort all pending requests and clear the cache of AbortControllers
   */
  private _destroyAbortControllers() {
    for (const controller of this._chatMessageAbortControllers.values()) {
      controller?.abort();
    }
    this._chatMessageAbortControllers.clear();
  }

  /**
   * When the DO is destroyed, cancel all pending requests
   */
  async destroy() {
    this._destroyAbortControllers();
    await super.destroy();
  }
}
